import { useRef, useState } from "react";
import { Share2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { avatarSrc } from "../../lib/api";
import { shareImage } from "../../lib/shareImage";
import { PoweredByMark } from "./PoweredBy";
import { shortDate } from "./kit";
import { Btn } from "./kit";
import { Modal } from "./Form";

const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/** 360px square branded card; exported at 3x = 1080x1080 for Instagram/X/Facebook. */
const SpotlightCard = ({ cardRef, tid, award, text, teamName, level, closing }) => {
  const [photo, setPhoto] = useState(true);
  return (
    <div ref={cardRef} data-testid="spotlight-card" className="relative h-[360px] w-[360px] shrink-0 overflow-hidden bg-[#0A0E1A] p-6 text-white" style={{ fontFamily: "inherit" }}>
      <div aria-hidden className="absolute -right-8 -top-12 font-display text-[200px] font-black leading-none text-[#39FF14]/[0.07]">7C</div>
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-2 bg-[#39FF14]" />
      <div className="relative flex h-full flex-col">
        <p className="font-display text-sm font-bold tracking-wide">GAME<span className="text-[#FF3B30]">DAY</span> <span className="font-mono-gd text-[8px] tracking-[0.3em] text-[#39FF14]">COURTSIDE BY HOOPSHQ</span></p>
        <p className="mt-4 font-mono-gd text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">7 C's Player of the Week</p>
        <p className="font-mono-gd text-[10px] uppercase tracking-[0.2em] text-slate-300">Week of {shortDate(award.week)}</p>
        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[#39FF14] bg-[#162032] font-display text-2xl font-black">
            {photo ? <img src={avatarSrc(tid, { id: award.player_id, has_app_photo: true })} alt="" crossOrigin="anonymous" onError={() => setPhoto(false)} className="h-full w-full object-cover" /> : initials(award.player_name)}
          </div>
          <p data-testid="spotlight-name" className="min-w-0 font-display text-3xl font-black uppercase leading-[0.95]">{award.player_name}</p>
        </div>
        <p className="mt-5 inline-block self-start rounded-full bg-[#39FF14] px-3 py-1 font-display text-xl font-black uppercase text-[#0A0E1A]">{award.c_name}</p>
        {text && <p className="mt-2 line-clamp-3 text-[11px] leading-snug text-slate-200">{text}</p>}
        <div className="mt-auto flex items-end justify-between gap-2 pb-1">
          <p className="font-display text-sm font-bold uppercase leading-tight">{teamName}{level === "jv" && !/\bjv\b/i.test(teamName) ? " JV" : ""}</p>
          {closing && <p className="max-w-[55%] text-right font-mono-gd text-[8px] uppercase tracking-wider text-[#39FF14]">{closing}</p>}
        </div>
        <div className="pb-2 text-right"><PoweredByMark testid="spotlight-powered" /></div>
      </div>
    </div>
  );
};

/** One tap: native share sheet with the image on phones; download on computers that can't share files. */
export const SpotlightShare = ({ tid, award, text, teamName, level, closing }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const fileName = `${award.player_name.replace(/[^a-z0-9]+/gi, "-")}-player-of-the-week.png`;
  const run = async (mode) => {
    setBusy(true);
    try {
      const caption = `${award.player_name} is our 7 C's Player of the Week for ${award.c_name}! #GAMEDAY`;
      const how = await shareImage(ref.current, { fileName, title: "Player of the Week", caption, mode });
      if (how === "downloaded") toast.success(mode === "share" ? "Image downloaded. Post it from your photos" : "Image downloaded");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the image. Please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Btn variant="ghost" data-testid="c-week-share-button" onClick={() => setOpen(true)}><Share2 className="h-4 w-4" />Share</Btn>
      <Modal open={open} onClose={() => setOpen(false)} testid="spotlight-dialog" title="Share Player of the Week" description="A square image sized for Instagram, Facebook and X.">
        <div className="mt-3 flex h-[300px] justify-center overflow-hidden rounded-xl border border-[#1E293B] sm:h-[362px]">
          <div className="origin-top scale-[0.83] sm:scale-100">
            <SpotlightCard cardRef={ref} tid={tid} award={award} text={text} teamName={teamName} level={level} closing={closing} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Btn variant="ghost" data-testid="spotlight-download" disabled={busy} onClick={() => run("download")}><Download className="h-4 w-4" />Download</Btn>
          <Btn data-testid="spotlight-share" disabled={busy} onClick={() => run("share")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}Share</Btn>
        </div>
      </Modal>
    </>
  );
};
